import React, { useState, useEffect, useCallback, FunctionComponent } from 'react';
import styled from 'styled-components';
import MentorFilter from './MentorFilter';
import MentorTable from './MentorTable';
import AdminDateFilterModal from '../common/AdminDateFilterModal';

export interface MentorData {
  id: number;
  name: string;
  email: string;
  department: string;
  tags: string[];
  image_url: string;
  banner_url: string;
  introduction: string;
  is_temp: boolean;
  created_at: string;
}

export interface MentorListResponse {
  code: number;
  status: string;
  message: string;
  data: {
    totalElements: number;
    totalPages: number;
    size: number;
    content: MentorData[];
    number: number;
  };
}

export interface FilterState {
  status: string;
  fromDate: string;
  toDate: string;
  searchTerm: string;
}

export interface PaginationState {
  page: number;
  size: number;
  totalPages: number;
  totalElements: number;
}

const MentorManagement: FunctionComponent = () => {
  const [mentors, setMentors] = useState<MentorData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [filter, setFilter] = useState<FilterState>({
    status: 'all',
    fromDate: '',
    toDate: '',
    searchTerm: '',
  });

  const [showDateModal, setShowDateModal] = useState(false);
  const [tempFromDate, setTempFromDate] = useState('');
  const [tempToDate, setTempToDate] = useState('');

  const [pagination, setPagination] = useState<PaginationState>({
    page: 0,
    size: 10,
    totalPages: 0,
    totalElements: 0,
  });

  const isValidDateFormat = useCallback((dateString: string): boolean => {
    const regex = /^\d{4}-\d{2}-\d{2}$/;
    if (!regex.test(dateString)) {
      return false;
    }

    const date = new Date(dateString);
    return date instanceof Date && !Number.isNaN(date.getTime()) && dateString === date.toISOString().split('T')[0];
  }, []);

  const fetchMentors = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const serverUrl = process.env.REACT_APP_CAMPUS_API_BASE_URL || 'https://campus-dev.duckdns.org/gateway';
      const apiUrl = `${serverUrl}/api/admin/mentors/list`;
      const params = new URLSearchParams();

      // 검색어가 있으면 list 엔드포인트의 search 파라미터로 검색
      if (filter.searchTerm.trim()) {
        params.append('search', filter.searchTerm.trim());
      }

      // /mentors/list 는 isTemp(임시저장 여부)로 필터링한다. temp=임시저장(true), approved=정식등록(false)
      if (filter.status === 'temp') {
        params.append('isTemp', 'true');
      } else if (filter.status === 'approved') {
        params.append('isTemp', 'false');
      }

      if (filter.fromDate && isValidDateFormat(filter.fromDate)) {
        params.append('from', filter.fromDate);
      }
      if (filter.toDate && isValidDateFormat(filter.toDate)) {
        params.append('to', filter.toDate);
      }

      params.append('page', pagination.page.toString());
      params.append('size', pagination.size.toString());

      const response = await fetch(`${apiUrl}?${params.toString()}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        let errorMessage = `응답 오류 (${response.status})`;

        try {
          const errorData = await response.json();
          if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch {
          errorMessage = `서버 오류가 발생했습니다 (${response.status})`;
        }

        setError(errorMessage);
        return;
      }

      const data: MentorListResponse = await response.json();
      setMentors(data.data.content);
      setPagination((prev) => ({
        ...prev,
        totalPages: data.data.totalPages,
        totalElements: data.data.totalElements,
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : '알 수 없는 오류가 발생했습니다.');
    } finally {
      setLoading(false);
    }
  }, [filter, pagination.page, pagination.size, isValidDateFormat]);

  useEffect(() => {
    fetchMentors();
  }, [fetchMentors]);

  const handleFilterChange = (newFilter: Partial<FilterState>) => {
    if (newFilter.fromDate !== undefined || newFilter.toDate !== undefined) {
      const fromDate = newFilter.fromDate !== undefined ? newFilter.fromDate : filter.fromDate;
      const toDate = newFilter.toDate !== undefined ? newFilter.toDate : filter.toDate;

      const isFromDateValid = !fromDate || isValidDateFormat(fromDate);
      const isToDateValid = !toDate || isValidDateFormat(toDate);

      if (isFromDateValid && isToDateValid) {
        if (fromDate && toDate && fromDate > toDate) {
          if (newFilter.fromDate !== undefined) {
            newFilter.toDate = '';
          } else {
            newFilter.fromDate = '';
          }
        }

        setFilter((prev) => ({ ...prev, ...newFilter }));
        setPagination((prev) => ({ ...prev, page: 0 }));
        return;
      }
    } else {
      setFilter((prev) => ({ ...prev, ...newFilter }));
      setPagination((prev) => ({ ...prev, page: 0 }));
    }
  };

  const handleEditMentor = (mentorId: number) => {
    const mentor = mentors.find((item) => item.id === mentorId);
    if (mentor) {
      localStorage.setItem('editingMentor', JSON.stringify(mentor));
    }
    window.location.href = `/admin/coffee-chat/mentor-edit?id=${mentorId}`;
  };

  const handleDeleteMentor = async (mentorId: number) => {
    if (!window.confirm('정말로 멘토를 삭제하시겠습니까?\n\n주의: 멘토 삭제 시 관련된 모든 커피챗 답변도 함께 삭제됩니다.')) {
      return;
    }

    try {
      setLoading(true);
      const serverUrl = process.env.REACT_APP_CAMPUS_API_BASE_URL || 'https://campus-dev.duckdns.org/gateway';
      const response = await fetch(`${serverUrl}/api/admin/mentors/${mentorId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('멘토 삭제에 실패했습니다.');
      }

      alert('멘토가 삭제되었습니다.');
      await fetchMentors();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : '멘토 삭제 중 오류가 발생했습니다.';
      setError(errorMessage);
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <MentorFilter
        filter={filter}
        onFilterChange={handleFilterChange}
        onDateModalOpen={() => {
          setTempFromDate(filter.fromDate);
          setTempToDate(filter.toDate);
          setShowDateModal(true);
        }}
      />

      <MentorTable
        mentors={mentors}
        loading={loading}
        error={error}
        onEdit={handleEditMentor}
        onDelete={handleDeleteMentor}
        currentPage={pagination.page + 1}
        totalPages={pagination.totalPages}
        onPageChange={(page) => setPagination((prev) => ({ ...prev, page: page - 1 }))}
      />

      {showDateModal && (
        <AdminDateFilterModal
          title="등록일 선택"
          startDate={tempFromDate}
          endDate={tempToDate}
          onStartDateChange={setTempFromDate}
          onEndDateChange={setTempToDate}
          onConfirm={() => {
            setFilter((prev) => ({ ...prev, fromDate: tempFromDate, toDate: tempToDate }));
            setPagination((prev) => ({ ...prev, page: 0 }));
            setShowDateModal(false);
          }}
          onCancel={() => setShowDateModal(false)}
        />
      )}
    </Container>
  );
};

const Container = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 32px;
`;

export default MentorManagement;
